import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      console.log('[AuthContext] Fetching current user with token:', token ? 'present' : 'missing');
      // Use V2 MySQL API endpoint
      const response = await axios.get(`${API_URL}/api/v2/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000  // 10 second timeout
      });
      console.log('[AuthContext] User response status:', response.status);
      if (response.data && response.data.data) {
        setUser(response.data.data);
        console.log('[AuthContext] User set successfully:', response.data.data.username);
      } else {
        console.error('[AuthContext] Invalid user response structure');
        logout();
      }
    } catch (error) {
      console.error('[AuthContext] Error fetching user:', error.message);
      // Clear invalid token
      logout();
    } finally {
      console.log('[AuthContext] Setting loading to false');
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      console.log('[AuthContext] Login attempt for:', username);
      // Use V2 MySQL API endpoint - identifier can be email or username
      const response = await axios.post(`${API_URL}/api/v2/auth/login`, {
        identifier: username,
        password
      });
      
      const { access_token, user } = response.data.data;
      console.log('[AuthContext] Login successful, setting token and user');
      setToken(access_token);
      setUser(user);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('[AuthContext] Token saved to localStorage');
      
      // Check if user is owner/kepala_cabang/teknisi/customer service and auto-set their branch
      const userRole = user.role?.toLowerCase();
      if ((userRole === 'owner' || userRole === 'kepala_cabang' || userRole === 'teknisi' || userRole === 'customer service') && user.branch_id) {
        // Set branch to user's assigned branch
        localStorage.setItem('selectedBranch', String(user.branch_id));
        window.dispatchEvent(new CustomEvent('setUserBranch', { detail: { branch_id: user.branch_id } }));
        console.log('[AuthContext] Set selectedBranch to user branch:', user.branch_id);
      } else {
        // Reset selectedBranch to 'all' for admin roles
        localStorage.setItem('selectedBranch', 'all');
        window.dispatchEvent(new CustomEvent('resetBranch'));
        console.log('[AuthContext] Reset selectedBranch to all');
      }
      
      // Dispatch event to reload branding for tenant
      window.dispatchEvent(new CustomEvent('userLoggedIn'));
      console.log('[AuthContext] Dispatched userLoggedIn event for branding refresh');
      
      return { success: true, user };
    } catch (error) {
      console.error('[AuthContext] Login failed:', error.message);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API_URL}/api/auth/register`, userData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rl_branding_cache_v1');
    // 2026-07-15: Clear ALL dashboard cache keys to prevent cross-user data pollution on relogin
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(k => {
        if (k.startsWith('dashboard_data_cache_')) {
          localStorage.removeItem(k);
        }
      });
    } catch { /* ignore */ }
    localStorage.setItem('selectedBranch', 'all');
    // Dispatch custom event to notify AppContext to reset branch
    window.dispatchEvent(new CustomEvent('resetBranch'));
  };

  const updateProfile = async (updates) => {
    try {
      const response = await axios.put(`${API_URL}/api/auth/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Update failed' 
      };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Password change failed' 
      };
    }
  };

  // Check if user has required roles OR permissions
  const hasPermission = (requiredRoles, requiredPermissions = []) => {
    if (!user) return false;
    
    const userRole = user.role?.toLowerCase();
    
    // ONLY super_admin has full access to everything
    if (['super_admin', 'superadmin'].includes(userRole)) {
      return true;
    }
    
    // Check by role name first (backward compatibility)
    if (requiredRoles && requiredRoles.length > 0) {
      if (requiredRoles.includes(userRole)) {
        return true;
      }
    }
    
    // Check by actual permissions from database
    const userPermissions = user.permissions || [];
    if (requiredPermissions && requiredPermissions.length > 0 && userPermissions.length > 0) {
      // User must have at least one of the required permissions
      return requiredPermissions.some(perm => userPermissions.includes(perm));
    }
    
    // If no roles matched and no permissions specified/matched, deny access
    return false;
  };
  
  // Check specific permission
  const hasSpecificPermission = (permission) => {
    if (!user) return false;
    // ONLY super_admin has full access to everything
    if (['super_admin', 'superadmin'].includes(user.role?.toLowerCase())) {
      return true;
    }
    const userPermissions = user.permissions || [];
    return userPermissions.includes(permission);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    hasPermission,
    hasSpecificPermission,
    refreshUser: fetchCurrentUser,
    isAuthenticated: !!(token && user)  // Both token AND user must exist
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};